/**
 * =============================================================================
 * TODO CHAT TYPE DEFINITIONS
 * =============================================================================
 * 
 * This file contains all TypeScript types for the Alert/Todo system.
 * 
 * API INTEGRATION GUIDE:
 * ----------------------
 * 1. Update API_CONFIG with your backend endpoints
 * 2. Update FIELD_MAPPINGS to map API response keys to UI fields
 * 3. Each type documents which API endpoint provides the data
 * 
 * STRUCTURE:
 * - Lines 20-80: Enums (AlertType, Priority, Status, ActionType)
 * - Lines 85-150: Core data types (Alert, Action, SupportingData)
 * - Lines 155-250: Chat message types
 * - Lines 255-350: API configuration and field mappings
 * - Lines 355-400: API response types
 */

// =============================================================================
// SECTION 1: ENUMS
// =============================================================================

/**
 * Alert categories - determines grouping on home page
 * API: Returned in alert.alertType field
 */
export enum AlertType {
    LOW_STOCK = 'low_stock',
    PO_APPROVAL = 'po_approval',
    GRN_PENDING = 'grn_pending',
    FORECAST_REVIEW = 'forecast_review',
    PAYMENT_DUE = 'payment_due',
    PERFORMANCE_REVIEW = 'performance_review',
    STOCK_EXPIRY = 'stock_expiry',
    ORDER_DELAY = 'order_delay',
    CUSTOM = 'custom',
}

/**
 * Priority levels - affects visual styling
 * API: Returned in alert.priority field
 */
export enum Priority {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high',
    URGENT = 'urgent',
}

/**
 * Alert status - determines which category tab to show alert in
 * API: Returned in alert.status field
 * 
 * HOME PAGE MAPPING:
 * - PENDING, IN_PROGRESS → "Pending Alerts" tab
 * - COMPLETED → "Done Alerts" tab
 * - SNOOZED → "Snoozed Alerts" tab
 * - CANCELLED → Hidden or "Done Alerts" tab
 */
export enum AlertStatus {
    PENDING = 'pending',
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'completed',
    SNOOZED = 'snoozed',
    CANCELLED = 'cancelled',
}

/**
 * Generated by - determines who created the alert
 * API: Returned in alert.generatedBy field
 * 
 * SYSTEM - Alerts created by the system automatically
 * USER - Alerts created by users manually
 */
export enum GeneratedBy {
    SYSTEM = 'SYSTEM',
    USER = 'USER',
}

/**
 * Action types that can be performed on alerts
 * API: Returned in alert.actions[].type field
 */
export enum ActionType {
    CREATE_SALE_ORDER = 'create_sale_order',
    SEND_EMAIL = 'send_email',
    CREATE_PO = 'create_po',
    CREATE_INTER_WH_TRANSFER = 'create_inter_wh_transfer',
    APPROVE = 'approve',
    REJECT = 'reject',
    MARK_COMPLETE = 'mark_complete',
    VIEW_DETAILS = 'view_details',
    ESCALATE = 'escalate',
    CUSTOM = 'custom',
}

/**
 * Chat log entry types - for rendering different log styles
 */
export enum ChatLogType {
    ACTION_SELECTED = 'action_selected',
    ACTION_CONFIRMED = 'action_confirmed',
    ACTION_REJECTED = 'action_rejected',
    ALERT_SNOOZED = 'alert_snoozed',
    ALERT_DELETED = 'alert_deleted',
    EDIT_STARTED = 'edit_started',
    EDIT_COMPLETED = 'edit_completed',
    EDIT_CANCELLED = 'edit_cancelled',
    SYSTEM = 'system',
}

// =============================================================================
// SECTION 2: CORE DATA TYPES
// =============================================================================

/**
 * Supporting data point - shown as bullet points in alert card
 * API: Returned in alert.supportingData[] array
 */
export interface SupportingDataPoint {
    /** Unique key for this data point */
    key: string;
    /** Display label */
    label: string;
    /** Value (can be string, number, or boolean) */
    value: string | number | boolean;
    /** Format hint for display: currency (₹), percentage (%), number, date, text */
    format?: 'currency' | 'percentage' | 'number' | 'date' | 'text';
}

/**
 * Context filters - extracted from data.filters.a.sourceParams
 * Shows the context/filters used to evaluate this alert
 */
/** Dynamic — any key-value pair from alert context / sourceParams */
export type AlertContextFilters = Record<string, unknown>;

/**
 * The deep link an alert carries back to the thing it was created from (a plan objective, a
 * channel plan, a rebalance scope, a filtered table). Written into the alert's context by the
 * surface that created it, copied onto every checklist item by the evaluator (data.artifact),
 * and turned into the card's button here — no per-alert-type code on either side.
 */
/** What was done with the artifact since the run (data.outcome), written back by the surface that owns it. */
export interface ArtifactOutcome {
    state: string;
    summary: string;
    at: string;
    by?: string | null;
}

export interface AlertArtifactLink {
    /** In-app route, e.g. "/replenishment". */
    route: string;
    /** Query params to append as-is. */
    params?: Record<string, string>;
    /** Query param filled from the breaching rows: `name=<csv of row[field]>`. */
    rowParam?: { name: string; field: string };
    /** Button text; the row count is appended when there are rows. */
    label?: string;
    /** Heading written when the run has nothing to report (makes the card show every run). */
    emptyHeading?: string;
}

/**
 * Breaching entity - an item that triggered the alert
 * Structure varies based on alert type (PO, SKU, etc.)
 */
export interface BreachingEntity {
    // =========================================================================
    // PO ALERT FIELDS (PO_CREATED, PO_APPROVAL, GRN_PENDING)
    // =========================================================================
    purchaseOrderId?: string;
    channel?: string;
    vendor?: string | null;
    itemsCount?: number;
    totalQuantity?: number;
    orderDate?: string;
    expiryDate?: string | null;
    status?: string;
    type?: string;
    warehouseId?: string;
    
    // =========================================================================
    // SKU ALERT FIELDS (SKU_UNDERSTOCK, SKU_OVERSTOCK)
    // =========================================================================
    product_id?: string;
    product_name?: string;
    sku?: string;
    quantity?: number;
    consumption_per_day?: number;
    consumption_per_month?: number;
    days_on_hand?: number;
    months_on_hand?: number;
    
    // =========================================================================
    // GENERIC FALLBACK
    // =========================================================================
    [key: string]: unknown;
}

/**
 * Action available on an alert
 * API: Returned in alert.actions[] array
 */
export interface AlertAction {
    /** Unique action ID */
    id: string;
    /** Action type enum */
    type: ActionType;
    /** Display label */
    label: string;
    /** Button style variant */
    variant?: 'primary' | 'secondary' | 'danger' | 'success';
    /** If true, shows confirmation card before executing */
    requiresConfirmation?: boolean;
    /** Message shown in confirmation card */
    confirmationMessage?: string;
    /** If true, action can be edited before confirming (opens iframe) */
    isEditable?: boolean;
    /** URL for iframe when editing action */
    editUrl?: string;
    /** API endpoint to call when action is executed */
    apiEndpoint?: string;
    /** HTTP method for the action */
    apiMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
}

/**
 * Action details - returned when user clicks an action button
 * API: GET /alerts/:alertId/actions/:actionId/details
 */
export interface ActionDetails {
    /** Action ID */
    actionId: string;
    /** Alert ID this action belongs to */
    alertId: string;
    /** Action label */
    label: string;
    /** Detailed description of what will happen */
    description: string;
    /** Data that will be affected/created */
    affectedData: SupportingDataPoint[];
    /** Confirmation message */
    confirmationMessage: string;
    /** If editable, the URL for the edit iframe */
    editUrl?: string;
    /** If editable, form fields that can be modified */
    editableFields?: Array<{
        key: string;
        label: string;
        type: 'text' | 'number' | 'date' | 'select';
        value: unknown;
        options?: Array<{ label: string; value: string }>;
    }>;
}

/**
 * Main Alert/Todo item
 * API: GET /alerts/list returns array of these
 */
export interface AlertItem {
    /** Unique identifier */
    id: string;
    /** Alert category type */
    alertType: AlertType;
    /** Human-readable alert type label */
    alertTypeLabel: string;
    /** Alert classification label (from alertLabel field) */
    alertLabel?: string;
    /** Title/headline */
    title: string;
    /** Brief description */
    description?: string;
    /** Priority level */
    priority: Priority;
    /** Current status (determines home page category) */
    status: AlertStatus;
    /** Supporting data points (shown only in expanded card) */
    supportingData: SupportingDataPoint[];
    /** Available actions */
    actions: AlertAction[];
    /** Created timestamp */
    createdAt: string;
    /** Last updated timestamp */
    updatedAt: string;
    /** Due date/time (alertActiveUntil) */
    dueDate?: string;
    /** Alert active from timestamp (alertActiveFrom) */
    alertActiveFrom?: string;
    /** Assignee name */
    assignee?: string;
    /** Source system/channel */
    source?: string;
    /** Related entity ID (PO number, SKU, etc.) */
    relatedEntityId?: string;
    /** If snoozed, until when */
    snoozedUntil?: string;
    /** Raw metadata from backend */
    metadata?: Record<string, unknown>;
    
    // =========================================================================
    // NEW: Parsed data from data JSON field
    // =========================================================================
    /** Context filters used to evaluate this alert (from data.filters.a) */
    contextFilters?: AlertContextFilters;
    /** Breaching entities that triggered the alert (from data.breachingEntities.a) */
    breachingEntities?: BreachingEntity[];
    /** Deep link back to the artifact this alert watches (from data.artifact). */
    artifact?: AlertArtifactLink;
    /** What was done with that artifact since the run (from data.outcome); cleared by the next run. */
    outcome?: ArtifactOutcome;
    /** Raw supporting data from supportingDataGenerator pipeline (from data.supportingData) */
    rawSupportingData?: Record<string, unknown>;
    /** Who generated this alert: SYSTEM or USER */
    generatedBy?: GeneratedBy;
    /**
     * Fields from $ctx.selectedEntity.* referenced across all generator steps.
     * If a breaching entity (or drill-down row) has any of these fields, it is clickable.
     * Derived by parsing supportingDataGenerator on the frontend — no extra API call needed.
     */
    entityLinkedFields?: string[];
    /**
     * Breach-entity fields the user agreed to see during alert creation
     * (from config.displayColumns). When set, the alert data table shows only
     * these columns instead of auto-detecting every field.
     */
    displayColumns?: string[];
}

// =============================================================================
// SECTION 3: CHAT MESSAGE TYPES
// =============================================================================

/**
 * Base interface for all chat messages
 */
interface BaseChatMessage {
    /** Unique message ID */
    id: string;
    /** Timestamp */
    timestamp: string;
}

/**
 * User action log entry - shows in chat as a log line
 */
export interface ChatLogMessage extends BaseChatMessage {
    type: 'log';
    /** Log type determines styling */
    logType: ChatLogType;
    /** Log message text */
    message: string;
    /** User who performed the action */
    user?: string;
}

/**
 * Action confirmation card message
 */
export interface ActionConfirmMessage extends BaseChatMessage {
    type: 'action_confirm';
    /** The action being confirmed */
    action: AlertAction;
    /** Details about the action (from API) */
    actionDetails: ActionDetails;
    /** The alert this action belongs to */
    alertId: string;
}

/**
 * Action result message (success/failure)
 */
export interface ActionResultMessage extends BaseChatMessage {
    type: 'action_result';
    /** The action that was executed */
    action: AlertAction;
    /** Success or failure */
    success: boolean;
    /** Result message */
    message: string;
}

/**
 * Iframe edit card message
 */
export interface IframeEditMessage extends BaseChatMessage {
    type: 'iframe_edit';
    /** The action being edited */
    action: AlertAction;
    /** URL to load in iframe */
    iframeUrl: string;
    /** Alert ID for context */
    alertId: string;
}

/**
 * Union type of all chat messages
 */
export type ChatMessage =
    | ChatLogMessage
    | ActionConfirmMessage
    | ActionResultMessage
    | IframeEditMessage;

// =============================================================================
// SECTION 4: API CONFIGURATION
// =============================================================================

/**
 * API endpoint configuration
 * UPDATE THESE VALUES when connecting to your backend
 */
export interface ApiEndpoints {
    /** Base URL for all API calls */
    baseUrl: string;
    
    /** GET - Get current user info (includes organizationId = merchant_id) */
    authMe: string;
    
    /** POST - Fetch all checklist items (requires merchant_id in body) */
    listAlerts: string;
    
    /** GET - Fetch single alert details */
    getAlert: string;
    
    /** GET - Fetch action details when user clicks action button */
    getActionDetails: string;
    
    /** POST - Execute an action */
    executeAction: string;
    
    /** POST - Snooze an alert */
    snoozeAlert: string;
    
    /** DELETE - Delete an alert */
    deleteAlert: string;
    
    /** POST - Update a checklist item */
    updateAlert: string;
    
    /** GET - Fetch activity logs for a checklist item */
    getActivityLogs: string;
    
    /** POST - Create a new activity log */
    createActivityLog: string;
}

/**
 * Default API endpoints - UPDATE THESE for your backend
 * 
 * AUTHENTICATION FLOW:
 * 1. JWT token is stored in localStorage under key "token"
 * 2. Call GET /auth/me to get organizationId (merchant_id)
 * 3. Pass merchant_id in body for checklist endpoints
 * 
 * NOTE: Uses relative URLs so:
 * - In LOCAL dev: Vite proxy forwards /api → localhost:8080
 * - In PRODUCTION: Relative URLs resolve to https://app.reparo.co.in/api
 */
export const API_ENDPOINTS: ApiEndpoints = {
    baseUrl: '/api/v1',
    
    // =========================================================================
    // AUTH ENDPOINT
    // Used to resolve merchant_id from JWT token
    // =========================================================================
    authMe: '/auth/me',                     // GET - Returns { organizationId: merchant_id }
    
    // =========================================================================
    // CHECK-LIST-ITEMS ENDPOINTS
    // All require merchant_id in request body
    // =========================================================================
    listAlerts: '/check-list-items/list',   // POST { merchant_id } → CheckListItemDto[]
    getAlert: '/alerts/:alertId',           // GET
    getActionDetails: '/alerts/:alertId/actions/:actionId/details', // GET
    executeAction: '/alerts/:alertId/actions/:actionId/execute',    // POST
    snoozeAlert: '/alerts/:alertId/snooze', // POST { minutes: number }
    deleteAlert: '/alerts/:alertId',        // DELETE
    updateAlert: '/check-list-items/update/:alertId', // POST - Update checklist item
    
    // =========================================================================
    // ACTIVITY LOG ENDPOINTS
    // =========================================================================
    getActivityLogs: '/check-list-logs/list/:checklistItemId', // GET - Fetch logs
    createActivityLog: '/check-list-logs/create', // POST { checklistItemId, logDetails, logType, userName }
};

/**
 * Field mapping from API response to UI
 * UPDATE THESE if your API uses different field names
 */
export interface FieldMapping {
    // Alert fields
    alertId: string;
    alertType: string;
    alertTypeLabel: string;
    title: string;
    description: string;
    priority: string;
    status: string;
    supportingData: string;
    actions: string;
    createdAt: string;
    updatedAt: string;
    dueDate: string;
    assignee: string;
    source: string;
    relatedEntityId: string;
    snoozedUntil: string;
    
    // Action fields (nested under actions array)
    actionId: string;
    actionType: string;
    actionLabel: string;
    actionVariant: string;
    actionRequiresConfirmation: string;
    actionConfirmationMessage: string;
    actionIsEditable: string;
    actionEditUrl: string;
    actionApiEndpoint: string;
    
    // Action details fields
    actionDetailsDescription: string;
    actionDetailsAffectedData: string;
    actionDetailsEditableFields: string;
}

/**
 * Default field mapping - Maps CheckListItemDto response keys to AlertItem fields
 * 
 * BACKEND CheckListItemDto fields:
 * - checklistItemId (uuid)
 * - merchantId (string)
 * - heading (string) → maps to title
 * - data (JSON string) → maps to supportingData (needs parsing)
 * - status (enum: CREATED, IGNORED, COMPLETED, SNOOZED, SEEN)
 * - actions (JSON string) → maps to actions (needs parsing)
 * - alertActiveFrom (date)
 * - alertActiveUntil (date)
 * - actionTaken (uuid)
 * - actionData (JSON string)
 */
export const DEFAULT_FIELD_MAPPING: FieldMapping = {
    // =========================================================================
    // CHECKLIST ITEM → ALERT FIELD MAPPING
    // =========================================================================
    alertId: 'checklistItemId',        // Backend: checklistItemId → Frontend: id
    alertType: 'alertType',            // Computed from alertEntity or default
    alertTypeLabel: 'alertTypeLabel',  // Computed from alertEntity or default
    title: 'heading',                  // Backend: heading → Frontend: title
    description: 'description',        // May need to extract from data JSON
    priority: 'priority',              // May need to extract from data JSON or default
    status: 'status',                  // Backend: status → Frontend: status (map enum)
    supportingData: 'data',            // Backend: data (JSON string) → needs parsing
    actions: 'actions',                // Backend: actions (JSON string) → needs parsing
    createdAt: 'createdAt',            // From AbstractEntity
    updatedAt: 'updatedAt',            // From AbstractEntity
    dueDate: 'alertActiveUntil',       // Backend: alertActiveUntil → Frontend: dueDate
    assignee: 'assignee',              // Not in backend, can be null
    source: 'source',                  // Not in backend, can be null
    relatedEntityId: 'alertId',        // Backend: alertId → links to UserAlert
    snoozedUntil: 'snoozedUntil',      // Not directly in backend
    
    // Action fields (from parsed actions JSON)
    actionId: 'id',
    actionType: 'type',
    actionLabel: 'label',
    actionVariant: 'variant',
    actionRequiresConfirmation: 'requiresConfirmation',
    actionConfirmationMessage: 'confirmationMessage',
    actionIsEditable: 'isEditable',
    actionEditUrl: 'editUrl',
    actionApiEndpoint: 'apiEndpoint',
    
    // Action details fields
    actionDetailsDescription: 'description',
    actionDetailsAffectedData: 'affectedData',
    actionDetailsEditableFields: 'editableFields',
};

/**
 * Snooze duration options
 */
export interface SnoozeOption {
    label: string;
    value: number; // minutes
}

export const SNOOZE_OPTIONS: SnoozeOption[] = [
    { label: '4 hours', value: 240 },
    { label: '1 day', value: 1440 },
    { label: '2 days', value: 2880 },
    { label: '1 week', value: 10080 },
];

// =============================================================================
// SECTION 5: API RESPONSE TYPES
// =============================================================================

/**
 * Response from GET /alerts/list
 */
export interface AlertListResponse {
    /** Array of alerts */
    data: AlertItem[];
    /** Total count */
    total: number;
    /** Pagination info */
    page: number;
    pageSize: number;
    hasNext: boolean;
}

/**
 * Response from GET /alerts/:alertId/actions/:actionId/details
 */
export interface ActionDetailsResponse {
    success: boolean;
    data: ActionDetails;
}

/**
 * Response from POST /alerts/:alertId/actions/:actionId/execute
 */
export interface ActionExecuteResponse {
    success: boolean;
    message: string;
    updatedAlert?: AlertItem;
}

/**
 * Response from POST /alerts/:alertId/snooze
 */
export interface SnoozeResponse {
    success: boolean;
    message: string;
    snoozedUntil: string;
}

/**
 * Response from DELETE /alerts/:alertId
 */
export interface DeleteResponse {
    success: boolean;
    message: string;
}

// =============================================================================
// SECTION 6: COMPONENT STATE TYPES
// =============================================================================

/**
 * Home page category tabs
 */
export type AlertCategory = 'pending' | 'done' | 'snoozed';

/**
 * Categorized alerts for home page display
 */
export interface CategorizedAlerts {
    pending: AlertItem[];
    done: AlertItem[];
    snoozed: AlertItem[];
}

/**
 * Chat window state
 */
export interface ChatState {
    /** The pinned alert at the top */
    pinnedAlert: AlertItem | null;
    /** Chat messages (logs, action cards, etc.) */
    messages: ChatMessage[];
    /** Currently pending action (awaiting confirmation) */
    pendingAction: AlertAction | null;
    /** Action details for pending action */
    pendingActionDetails: ActionDetails | null;
    /** Is iframe edit mode active */
    isEditing: boolean;
    /** Current edit iframe URL */
    editIframeUrl: string | null;
    /** Loading state */
    isLoading: boolean;
}

/**
 * Current user info (for logs)
 */
export interface CurrentUser {
    id: string;
    name: string;
    email: string;
}
